// src/api_calls.js
import axios from "axios";
import keycloak from "./keycloak.js";

/* URL base de FastAPI. Vite injects VITE_API_URL at build time. */
export const API_URL = (import.meta.env?.VITE_API_URL || globalThis.process?.env?.VITE_API_URL || "/api").replace(/\/$/, "");

/* Base específica para los endpoints de usuarios */
export const USERS_API = API_URL;

function isApiRequest(url) {
  return typeof url === "string" && (url.startsWith(API_URL) || url.startsWith("/api/"));
}

export async function getAuthHeaders({ requireAuth = true } = {}) {
  if (!keycloak.authenticated) {
    if (requireAuth) {
      throw new Error("User not authenticated");
    }
    return {};
  }

  try {
    await keycloak.updateToken(30);
  } catch (err) {
    console.error("Token refresh failed", err);
  }

  return keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {};
}

let axiosAuthInterceptorInstalled = false;

function ensureAxiosAuthInterceptor() {
  if (axiosAuthInterceptorInstalled) return;

  axios.interceptors.request.use(async (config) => {
    const nextConfig = { ...config };
    const headers = { ...(nextConfig.headers || {}) };
    const shouldAttachAuth = nextConfig.skipAuth !== true && isApiRequest(nextConfig.url || "");

    if (shouldAttachAuth) {
      const authHeaders = await getAuthHeaders();
      Object.assign(headers, authHeaders);
      nextConfig.withCredentials = true;
    }

    nextConfig.headers = headers;
    return nextConfig;
  });

  axiosAuthInterceptorInstalled = true;
}

ensureAxiosAuthInterceptor();

export async function authFetch(input, init = {}, { requireAuth = true } = {}) {
  const headers = new Headers(init.headers || {});
  const authHeaders = await getAuthHeaders({ requireAuth });

  Object.entries(authHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });

  return fetch(input, {
    ...init,
    credentials: init.credentials || "include",
    headers,
  });
}

/* ===========================
   SURVEY CALLS
   =========================== */

export async function fetchSurveyData(surveyId) {
  const url = surveyId
    ? `${API_URL}/survey/${surveyId}`
    : `${API_URL}/survey`;
  const headers = await getAuthHeaders();
  const response = await axios.get(url, { withCredentials: true, headers });
  return response.data;
}

export async function fetchCurrentSurveyData() {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_URL}/survey/current`, { withCredentials: true, headers });
  return response.data;
}

export async function fetchSurveySummaryData() {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_URL}/survey/summary`, { withCredentials: true, headers });
  return response.data;
}

export async function createSurveyData(newSurvey) {
  const headers = await getAuthHeaders();
  const response = await axios.post(`${API_URL}/survey`, newSurvey, {
    headers: { "Content-Type": "application/json", ...headers },
    withCredentials: true,
  });
  return response.data;
}

export async function updateSurveyData(surveyId, updatedSurvey) {
  const headers = await getAuthHeaders();
  const response = await axios.put(
    `${API_URL}/survey/${surveyId}`,
    updatedSurvey,
    {
      headers: { "Content-Type": "application/json", ...headers },
      withCredentials: true,
    }
  );
  return response.data;
}

export async function deleteSurveyData(id) {
  const headers = await getAuthHeaders();
  const response = await axios.delete(`${API_URL}/survey/${id}`, { withCredentials: true, headers });
  return response.data;
}

/* ===========================
   AVATAR CALLS
   =========================== */

/**
 * 1) saveAvatarFormData: sube o edita multipart/form-data en /avatars
 *    recibe un FormData con campo "file" y "username"
 */
export async function saveAvatarFormData(formData, avatarId) {
  const url = avatarId
    ? `${API_URL}/avatars/${avatarId}`
    : `${API_URL}/avatars`;
  const method = avatarId ? "put" : "post";

  const response = await axios({
    url,
    method,
    data: formData,
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" }
  });
  return response.data;
}

/**
 * 2) uploadImageToMinIO: sube binario a MinIO con POST /files/file
 */
export const uploadImageToMinIO = async (imageFile) => {
  const formData = new FormData();
  formData.append("file", imageFile);
  const response = await authFetch(`${API_URL}/files/file`, {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    throw new Error("Error subiendo imagen a MinIO");
  }
  const data = await response.json();
  return data.fileName;
}

/* ===========================
   BLOQUES / OTROS
   =========================== */

export const updateBlockData = (surveyData, blockId, updatedData) => ({
  ...surveyData,
  [`bloc${blockId}`]: updatedData,
});
